import React, { useState, useEffect, useCallback } from 'react';
import { FiBell, FiArrowLeft, FiTrash2, FiCheck, FiCheckCircle, FiFilter, FiRefreshCw, FiSettings } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../utils/toast';
import Swal from 'sweetalert2';
import notificationApi from '../../services/notificationApi';

const NotificationScreen = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all'); // all, read, unread
  const [typeFilter, setTypeFilter] = useState('all'); // all, appointment, system, etc.
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    fetchNotifications(true);
  }, [filter, typeFilter]);

  const fetchNotifications = async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setPage(1);
      } else {
        setLoadingMore(true);
      }

      const currentPage = reset ? 1 : page;
      const params = {
        page: currentPage,
        per_page: 20,
        ...(filter !== 'all' && { status: filter }),
        ...(typeFilter !== 'all' && { type: typeFilter })
      };

      const response = await notificationApi.getNotifications(params);
      const newNotifications = response.data || [];

      if (reset) {
        setNotifications(newNotifications);
      } else {
        setNotifications(prev => [...prev, ...newNotifications]);
      }

      setHasMore(newNotifications.length === 20);
      if (!reset) setPage(prev => prev + 1);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchNotifications(true);
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, read_at: new Date().toISOString() }
            : notif
        )
      );
      toast.success('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev =>
        prev.map(notif => ({
          ...notif,
          read_at: notif.read_at || new Date().toISOString()
        }))
      );
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    const result = await Swal.fire({
      title: 'Delete Notification?',
      text: 'Are you sure you want to delete this notification? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'var(--color-primary, #087F4C)',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: 'swal2-confirm-button',
        cancelButton: 'swal2-cancel-button'
      },
      buttonsStyling: false,
      didOpen: () => {
        // Add custom styles to ensure buttons are visible
        const style = document.createElement('style');
        style.textContent = `
          .swal2-confirm-button {
            background-color: var(--color-primary, #087F4C) !important;
            color: white !important;
            border: none !important;
            padding: 10px 20px !important;
            border-radius: 6px !important;
            font-weight: 500 !important;
            margin: 0 5px !important;
            cursor: pointer !important;
          }
          .swal2-cancel-button {
            background-color: #d33 !important;
            color: white !important;
            border: none !important;
            padding: 10px 20px !important;
            border-radius: 6px !important;
            font-weight: 500 !important;
            margin: 0 5px !important;
            cursor: pointer !important;
          }
          .swal2-confirm-button:hover {
            background-color: #065a36 !important;
          }
          .swal2-cancel-button:hover {
            background-color: #b02a2a !important;
          }
        `;
        document.head.appendChild(style);
      }
    });

    if (result.isConfirmed) {
      try {
        await notificationApi.deleteNotification(notificationId);
        setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
        toast.success('Notification deleted');
      } catch (error) {
        console.error('Error deleting notification:', error);
        toast.error('Failed to delete notification');
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment':
        return '📅';
      case 'system':
        return '⚙️';
      case 'reminder':
        return '⏰';
      default:
        return '📢';
    }
  };

  const filteredNotifications = notifications;
  const unreadCount = notifications.filter(n => !n.read_at).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FiArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
            <div className="flex items-center space-x-2">
              <h1 className="text-xl font-semibold text-gray-900">Notifications</h1>
              {unreadCount > 0 && (
                <span className="bg-primary100 text-white text-xs px-2 py-1 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            <button
              onClick={() => navigate('/notification-settings')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FiSettings className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Action Bar */}
          <div className="flex items-center justify-between mt-4 space-x-2">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center space-x-1 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="text-sm">Refresh</span>
              </button>

              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="flex items-center space-x-1 px-3 py-2 bg-primary100 text-white rounded-lg hover:bg-primary100/90 transition-colors"
                >
                  <FiCheckCircle className="w-4 h-4" />
                  <span className="text-sm">Mark All Read</span>
                </button>
              )}
            </div>

            {/* Filter Dropdown */}
            <div className="flex items-center space-x-2">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary100"
              >
                <option value="all">All</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-md mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary100"></div>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="text-center py-12">
            <FiBell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications</h3>
            <p className="text-gray-500">
              {filter === 'unread' ? "No unread notifications" : "You're all caught up!"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`bg-white rounded-lg p-4 shadow-sm border transition-all ${
                  notification.read_at 
                    ? 'border-gray-200' 
                    : 'border-primary100 bg-primary100/5 shadow-md'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-start space-x-3 flex-1">
                    <span className="text-lg mt-1">
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">
                        {notification.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-2">{notification.body}</p>
                      <div className="text-xs text-gray-400">
                        {formatDate(notification.created_at)}
                      </div>
                    </div>
                  </div>
                  
                  {!notification.read_at && (
                    <div className="w-2 h-2 bg-primary100 rounded-full ml-2 mt-2"></div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-2 mt-3 pt-3 border-t border-gray-100">
                  {!notification.read_at && (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="flex items-center space-x-1 px-3 py-1 text-primary100 hover:bg-primary100/10 rounded-lg transition-colors"
                    >
                      <FiCheck className="w-4 h-4" />
                      <span className="text-sm">Mark Read</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => deleteNotification(notification.id)}
                    className="flex items-center space-x-1 px-3 py-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FiTrash2 className="w-4 h-4" />
                    <span className="text-sm">Delete</span>
                  </button>
                </div>
              </div>
            ))}

            {/* Load More Button */}
            {hasMore && (
              <div className="text-center py-4">
                <button
                  onClick={() => fetchNotifications(false)}
                  disabled={loadingMore}
                  className="px-6 py-2 bg-primary100 text-white rounded-lg hover:bg-primary100/90 transition-colors disabled:opacity-50"
                >
                  {loadingMore ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Loading...</span>
                    </div>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationScreen;
